<template>
  <v-select
    v-model="frequency"
    :items="page.frequencies"
    item-text="name"
    item-value="id"
    label="Fréquence"
    outlined
    hide-details
  />
</template>

<script>
import { computed, onMounted, reactive, set, watch } from "@vue/composition-api";
import isEqual from "lodash/isEqual";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

const FREQUENCY_DISPLAYABLE_TEXTS = {
  hourly: "Toutes les heures",
  bidaily: "2 fois / jour",
};

export default {
  name: "FrequencyFilter",
  components: {},
  props: {
    initialValue: {
      type: Number,
      validator(value) {
        return !isNaN(value);
      },
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return ["aiFactory"].includes(value);
      },
    },
    storeGetter: {
      type: String,
      default: "getDagFrequency",
    },
    storeUpdater: {
      type: String,
      default: "updateDagFrequency",
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    forceSiteId: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: "", // in case we need to load from url params
      frequencies: [],
    });

    // =>  METHODS
    const initFrequencies = async () => {
      const { data: { items } } = await axiosGet("/aif-auto-configs/frequencies", {
            ...(props.forceSiteId > 0 
              ? { site_id: props.forceSiteId } 
              : site.value ? { site_id: site.value } : {}),
          });

      page.frequencies = items.map((item) => {
        if (item.name in FREQUENCY_DISPLAYABLE_TEXTS) {
          return {
            ...item,
            name: FREQUENCY_DISPLAYABLE_TEXTS[item.name],
          };
        }

        return item;
      });
    };

    // =>  COMPUTED
    const viaStore = computed(() => props.store !== undefined);
    const initialValue = computed(() =>
      props.noUrl
        ? props.initialValue
        : page.initialUrlValue ?? props.initialValue
    );
    const site = computed(() => store.getters["newsRadar/getSites"]);

    const frequency = computed({
      get: () => {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        return initialValue.value;
      },
      set: (value) => {
        if (viaStore.value) {
          store.dispatch(`${props.store}/${props.storeUpdater}`, value);

          if (page.isInitialized && !props.noUrl) {
            // Only update user changes, not initialization
            addQueryStringParam({
              param: "frequency",
              value: value,
              // is_multiple: false,
              // is_integer: false,
              // is_boolean: false,
            });
          }
        }

        if (!isEqual(value, initialValue.value)) {
          emit("valueChanged", value);
        }
      },
    });

    // =>  WATCH
    watch(site, async () => {
      await initFrequencies();
    });
    watch(() => props.forceSiteId, async () => {
      await initFrequencies();
    });

    // =>  HOOKS
    onMounted(async () => {
      await initFrequencies();

      if (viaStore.value && !props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: "aiFactory/updateDagFrequency",
            param: "frequency",
            value: frequency.value,
            // is_multiple: false,
            // is_integer: false,
            // is_boolean: false,
            // dependsOn: undefined,
          })
        );
      }

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      page,
      frequency,
    };
  },
};
</script>

<style></style>
